import React, { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataContext } from 'context/DataStoreContext';
import { useApi } from 'model/Api';
import { User } from 'model/User';
import { LineRelation } from 'model/Relation';
import { generalStyle } from 'helper/Style';
import { convertDate, convertUserName } from 'helper/Converter';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import Modal, { modalStyle } from 'component/part/Modal';
import H2 from 'component/part/H2';
import List from 'component/part/List';

const relationConditions = { with: ['user'] };
const userConditions = { with: ['role', 'relation'] };

const relationIndexes = [
  { label: '選択', key: '' },
  { label: '受信日時', key: 'id' },
  { label: 'LINEユーザー名' },
  { label: '連携済', key: 'userId' },
];

const userIndexes = [
  { label: '選択', key: '' },
  { label: '名前', key: 'lastName' },
  { label: '役職', key: 'role' },
  { label: '連携済', key: '' },
];

const style = {
  formArea: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    justifyContent: 'flex-start',
    alignItems: 'top',
    gap: '4rem',
    ['& > div:ntf-of-type(3)']: {
      gridColumn: '1 / 3',
    },
  },
  selected: {
    ['& > div']: {
      backgroundColor: '#ffcccc',
    },
  },
};

const LineRelationSet: React.FC = () => {
  const { state } = useDataContext();
  const { list: relations = [] } = state?.lineRelation || {};
  const { list: users = [] } = state?.user || {};

  const api = useApi();
  const navigate = useNavigate();
  const [relation, setRelation] = useState<LineRelation>();
  const [user, setUser] = useState<User>();
  const [confirmStatus, setConfirmStatus] = useState(0);
  const [resultStatus, setResultStatus] = useState(0);

  const onConfirm = () => {
    if (relation && user) {
      setConfirmStatus(1);
    }
  };

  const onSubmit = async () => {
    if (relation && user) {
        await api.lineRelation.relate(relation.id, user.id);
        setConfirmStatus(0);
        setResultStatus(1);
    }
  };

  const onUnderstoodResult = () => {
    setResultStatus(0);
    if (resultStatus === 2) {
      navigate('/line-relation/list');
    }
  };

  return <Layout>
    <BackLink link='/line-relation/list' label='メンバー一覧'></BackLink>
    <H2>LINE連携</H2>
    <div css={style.formArea}>
      <div css={generalStyle.form}>
        <List
          type='lineRelation'
          indexes={relationIndexes}
          conditions={relationConditions}
          displayPager={user?.role?.permissions.userEdit}
          css={generalStyle.list(4, { center: [1, 4] })}
        >
          {relations.map((item, index) => (
            <label css={[generalStyle.listRow, ...(item === relation ? [style.selected] : [])]} key={index}>
              <div><input type='radio' onChange={() => setRelation(item)} checked={item === relation} /></div>
              <div>{convertDate(item.createDatetime)}</div>
              <div>{item.platformUserName}</div>
              <div>{item.userId ? '済' : 'ー'}</div>
            </label>
          ))}
        </List>
      </div>
      <div css={generalStyle.form}>
        <List
          type='user'
          indexes={userIndexes}
          conditions={userConditions}
          css={generalStyle.list(4, { center: [1, 4] })}
        >
          {users.map((item, index) => (
            <label css={[generalStyle.listRow, ...(item.id === user?.id ? [style.selected] : [])]} key={index}>
              <div><input type='radio' onChange={() => setUser(item)} checked={item.id === user?.id} /></div>
              <div>{convertUserName(item)}</div>
              <div>{item.role?.name}</div>
              <div>{item.relation ? '済' : 'ー'}</div>
            </label>
          ))}
        </List>
      </div>

      <div css={generalStyle.form}>
        <div css={generalStyle.formRow}>
          <button
            disabled={!relation || !user}
            onClick={onConfirm}
            css={generalStyle.button('normal', !relation || !user)}
          >
            連携
          </button>
        </div>
      </div>
    </div>
    <Modal display={!!confirmStatus} onHide={() => setConfirmStatus(0)}>
      <div>選択したユーザーにLINEアカウントを連携させます</div>
      <Fragment>
        {relation?.user && (
          <div>※現在LINEアカウントに設定中の {convertUserName(relation.user)} さんとの連携は解除されます</div>
        )}
        {user?.relation && (
          <div>※現在{convertUserName(user)}さんに設定中の「{user.relation.platformUserName}」との連携は解除されます</div>
        )}
      </Fragment>
      <div css={modalStyle.buttonArea}>
        <button onClick={onSubmit} css={generalStyle.button()}>連携</button>
        <button onClick={() => setConfirmStatus(0)} css={generalStyle.button('warning')}>キャンセル</button>
      </div>
    </Modal>
    <Modal display={!!resultStatus} onHide={() => setResultStatus(0)}>
      <div>連携が完了しました</div>
      <div css={modalStyle.buttonArea}>
        <button onClick={onUnderstoodResult} css={generalStyle.button()}>OK</button>
      </div>
    </Modal>
  </Layout>;
};

export default LineRelationSet;
